import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  makeStyles,
  shorthands,
  tokens,
  TabList,
  Tab,
  Spinner,
  Badge,
  Portal,
  FluentProvider,
} from '@fluentui/react-components';
import { CalendarRegular } from '@fluentui/react-icons';
import { apiFetch } from '../lib/apiFetch';
import { useTheme } from '../contexts/ThemeContext';

interface CustomerHoverCardProps {
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerType?: string;
  securityDeposit?: string;
  bookings?: number;
  revenue?: number;
  customerSince?: string;
  anchorRef: React.RefObject<HTMLElement>;
  isVisible: boolean;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

interface CurrentOrder {
  id: string;
  number: number;
  status: string;
  starts_at: string;
  stops_at: string;
}

const useStyles = makeStyles({
  card: {
    position: 'fixed',
    width: '360px',
    backgroundColor: '#ffffff',
    boxShadow: tokens.shadow64,
    borderRadius: '8px',
    // No outer border — the elevated shadow provides separation. Inner
    // horizontal dividers use colorNeutralStroke2 to match BaseDialog's
    // header/footer.
    // Fluent's Drawer/Dialog overlay sits at z-index ~1000000 in v9; the
    // hover card needs to clear that to peek out over a drawer surface.
    zIndex: 1000001,
    display: 'flex',
    flexDirection: 'column',
    cursor: 'default',
    pointerEvents: 'auto',
    transitionProperty: 'opacity, transform',
    transitionDuration: '200ms',
    transitionTimingFunction: 'ease-in-out',
  },
  cardEntering: {
    opacity: 0,
    transform: 'translateX(-10px)',
  },
  cardEntered: {
    opacity: 1,
    transform: 'translateX(0)',
  },
  cardExiting: {
    opacity: 0,
    transform: 'translateX(-10px)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('16px', '16px', '12px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  avatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#0078d4',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 600,
    flexShrink: 0,
  },
  headerInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    flex: 1,
  },
  name: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#242424',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  email: {
    fontSize: '13px',
    color: '#605e5c',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  tabsContainer: {
    ...shorthands.padding('0', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  content: {
    ...shorthands.padding('16px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#242424',
  },
  value: {
    fontSize: '14px',
    color: '#605e5c',
  },
  footer: {
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  viewButton: {
    width: '100%',
  },
  orderItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('6px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  orderNumber: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#242424',
  },
  orderDates: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#605e5c',
  },
  emptyState: {
    textAlign: 'center',
    color: '#605e5c',
    fontSize: '14px',
    ...shorthands.padding('20px', '0'),
  },
});

const CustomerHoverCard: React.FC<CustomerHoverCardProps> = ({
  customerId,
  customerName,
  customerEmail,
  customerType = 'Individual',
  securityDeposit = 'Default - None',
  bookings = 0,
  revenue = 0,
  customerSince,
  anchorRef,
  isVisible,
  onClose,
  onMouseEnter,
  onMouseLeave,
}) => {
  const styles = useStyles();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>('info');
  const [animationState, setAnimationState] = useState<'entering' | 'entered' | 'exiting'>('entering');
  const [shouldRender, setShouldRender] = useState(false);
  const [currentOrders, setCurrentOrders] = useState<CurrentOrder[]>([]);
  const [currentOrdersLoading, setCurrentOrdersLoading] = useState(false);
  const [currentOrdersError, setCurrentOrdersError] = useState<string | null>(null);

  // Manage rendering - show immediately when visible
  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
    } else if (!isVisible && shouldRender) {
      // Wait for exit animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 200); // Match the transition duration
      return () => clearTimeout(timer);
    }
  }, [isVisible, shouldRender]);

  // Manage animation state
  useEffect(() => {
    if (isVisible && isPositioned) {
      // Start entering animation
      setAnimationState('entering');
      // After a short delay, transition to entered state
      const timer = setTimeout(() => {
        setAnimationState('entered');
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isVisible) {
      // Start exiting animation
      setAnimationState('exiting');
    }
  }, [isVisible, isPositioned]);

  // Calculate position based on anchor element
  useEffect(() => {
    if (isVisible && anchorRef.current && cardRef.current) {
      // Wait for card to render with content before calculating position
      requestAnimationFrame(() => {
        if (!cardRef.current || !anchorRef.current) return;

        const anchorRect = anchorRef.current.getBoundingClientRect();
        const cardRect = cardRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        console.log('Anchor rect:', anchorRect);
        console.log('Card rect:', cardRect);
        console.log('Viewport:', { viewportWidth, viewportHeight });

        // If card hasn't rendered yet (height is 0), wait a bit more
        if (cardRect.height === 0) {
          setTimeout(() => {
            if (!cardRef.current || !anchorRef.current) return;
            const anchorRect = anchorRef.current.getBoundingClientRect();
            const cardRect = cardRef.current.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;

            console.log('DELAYED - Anchor rect:', anchorRect);
            console.log('DELAYED - Card rect:', cardRect);
            console.log('DELAYED - Viewport:', { vw, vh });

            // Default: card's bottom-left corner sits at the anchor's
            // top-right corner (card floats above-right of the name).
            let left = anchorRect.right;
            let top = anchorRect.top - cardRect.height;

            console.log('DELAYED - Initial calc:', {
              anchorRight: anchorRect.right,
              left,
              'left + cardWidth': left + cardRect.width,
              'viewport - 16': vw - 16
            });

            // Off the right edge: flip so the card's bottom-right sits at
            // the anchor's top-left.
            if (left + cardRect.width > vw - 16) {
              console.log('DELAYED - Would go off right, flipping to left side');
              left = anchorRect.left - cardRect.width;
            }

            // Off the top edge: drop the card below the anchor instead
            // (top-left at anchor's bottom-left).
            if (top < 16) {
              top = anchorRect.bottom;
            }

            // Clamp to viewport.
            if (left < 16) left = 16;
            if (top + cardRect.height > vh - 16) top = vh - cardRect.height - 16;
            if (top < 16) top = 16;

            console.log('DELAYED - Final position:', { top, left });
            setPosition({ top, left });
            setIsPositioned(true);
          }, 50);
          return;
        }

        // Default: card's bottom-left corner sits at the anchor's
        // top-right corner (card floats above-right of the name).
        let left = anchorRect.right;
        let top = anchorRect.top - cardRect.height;

        console.log('Initial position:', { top, left });

        // Off the right edge: flip so the card's bottom-right sits at
        // the anchor's top-left.
        if (left + cardRect.width > viewportWidth - 16) {
          left = anchorRect.left - cardRect.width;
          console.log('Flipped to left side:', left);
        }

        // Off the top edge: drop the card below the anchor instead
        // (top-left at anchor's bottom-left).
        if (top < 16) {
          top = anchorRect.bottom;
          console.log('Flipped below anchor:', top);
        }

        // Clamp to viewport.
        if (left < 16) {
          left = 16;
          console.log('Adjusted left edge:', left);
        }
        if (top + cardRect.height > viewportHeight - 16) {
          top = viewportHeight - cardRect.height - 16;
          console.log('Adjusted bottom edge:', top);
        }
        if (top < 16) {
          top = 16;
          console.log('Adjusted top edge:', top);
        }

        console.log('Final position:', { top, left });

        setPosition({ top, left });
        setIsPositioned(true);
      });
    }
  }, [isVisible, anchorRef, shouldRender]);

  // Reset positioned state when card is unmounted
  useEffect(() => {
    if (!shouldRender) {
      setIsPositioned(false);
    }
  }, [shouldRender]);

  // Fetch current orders when Current tab is selected
  useEffect(() => {
    const fetchCurrentOrders = async () => {
      if (selectedTab !== 'current' || !customerId || !isVisible) {
        return;
      }

      try {
        setCurrentOrdersLoading(true);
        const response = await apiFetch(`/get-customer-orders/${customerId}`);

        if (!response.ok) {
          throw new Error('Failed to fetch orders');
        }

        const result = await response.json();

        // Filter for current orders (reserved, started, or stopped status)
        const allOrders = (result.data || []).map((order: any) => {
          const attrs = order.attributes || {};
          return {
            id: order.id,
            number: attrs.number || 0,
            status: attrs.status || 'unknown',
            starts_at: attrs.starts_at || '',
            stops_at: attrs.stops_at || '',
          };
        });

        // Only show active orders (not returned, canceled, or archived)
        const activeOrders = allOrders.filter((order: CurrentOrder) => {
          const status = order.status.toLowerCase();
          return status !== 'returned' && status !== 'canceled' && status !== 'cancelled' && status !== 'archived';
        });

        setCurrentOrders(activeOrders);
        setCurrentOrdersError(null);
      } catch (err) {
        console.error('Error fetching current orders:', err);
        setCurrentOrdersError(err instanceof Error ? err.message : 'Failed to fetch orders');
      } finally {
        setCurrentOrdersLoading(false);
      }
    };

    fetchCurrentOrders();
  }, [selectedTab, customerId, isVisible]);

  // Get initials for avatar
  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  // Get status badge styling
  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'reserved' || statusLower === 'new') {
      return { color: 'informative' as const, style: { backgroundColor: '#1890ff' } };
    } else if (statusLower === 'started' || statusLower === 'in_progress') {
      return { color: 'warning' as const, style: { backgroundColor: '#faad14' } };
    }
    return { color: 'subtle' as const, style: { backgroundColor: '#8c8c8c' } };
  };

  const handleViewCustomer = () => {
    navigate(`/customers/${customerId}`);
    onClose();
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent click events from propagating to parent elements (like table rows)
    e.stopPropagation();
  };

  if (!shouldRender) return null;

  const getAnimationClass = () => {
    switch (animationState) {
      case 'entering':
        return styles.cardEntering;
      case 'entered':
        return styles.cardEntered;
      case 'exiting':
        return styles.cardExiting;
      default:
        return '';
    }
  };

  // <Portal> mounts into document.body so the card escapes the Drawer's
  // transformed ancestor. Inside the portal, we re-wrap with FluentProvider
  // because Portal alone does NOT carry the theme: tokens like shadow64 are
  // CSS variables scoped to the outer FluentProvider element, and the
  // portal target (document.body) is outside that scope. Wrapping again
  // re-emits the variables so every token resolves correctly.
  return (
    <Portal>
    <FluentProvider theme={theme}>
    <div
      ref={cardRef}
      className={`${styles.card} ${getAnimationClass()}`}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={handleCardClick}
    >
      <div className={styles.header}>
        <div className={styles.avatar}>{getInitials(customerName)}</div>
        <div className={styles.headerInfo}>
          <div className={styles.name}>{customerName}</div>
          <div className={styles.email}>{customerEmail || '—'}</div>
        </div>
      </div>

      <div className={styles.tabsContainer}>
        <TabList
          selectedValue={selectedTab}
          onTabSelect={(_, data) => setSelectedTab(data.value as string)}
        >
          <Tab value="info">Info</Tab>
          <Tab value="current">Current ({currentOrders.length})</Tab>
          <Tab value="history">History (0)</Tab>
          <Tab value="notes">Notes (0)</Tab>
        </TabList>
      </div>

      <div className={styles.content}>
        {selectedTab === 'info' && (
          <>
            <div className={styles.infoRow}>
              <div className={styles.label}>Email</div>
              <div className={styles.value}>{customerEmail}</div>
            </div>

            <div className={styles.infoRow}>
              <div className={styles.label}>Customer type</div>
              <div className={styles.value}>{customerType}</div>
            </div>

            <div className={styles.infoRow}>
              <div className={styles.label}>Security deposit</div>
              <div className={styles.value}>{securityDeposit}</div>
            </div>

            <div className={styles.infoRow}>
              <div className={styles.label}>Bookings</div>
              <div className={styles.value}>{bookings}</div>
            </div>

            <div className={styles.infoRow}>
              <div className={styles.label}>Revenue</div>
              <div className={styles.value}>${revenue.toFixed(2)}</div>
            </div>

            {customerSince && (
              <div className={styles.infoRow}>
                <div className={styles.label}>Customer since</div>
                <div className={styles.value}>{customerSince}</div>
              </div>
            )}
          </>
        )}

        {selectedTab === 'current' && (
          <>
            {currentOrdersLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <Spinner size="small" />
              </div>
            )}

            {currentOrdersError && (
              <div className={styles.emptyState} style={{ color: '#c42b1c' }}>
                Error loading orders
              </div>
            )}

            {!currentOrdersLoading && !currentOrdersError && currentOrders.length === 0 && (
              <div className={styles.emptyState}>No current orders</div>
            )}

            {!currentOrdersLoading && !currentOrdersError && currentOrders.length > 0 && (
              <>
                {currentOrders.map((order) => {
                  const statusBadge = getStatusBadge(order.status);
                  return (
                    <div key={order.id} className={styles.orderItem}>
                      <div className={styles.orderHeader}>
                        <div className={styles.orderNumber}>#{order.number}</div>
                        <Badge appearance="filled" color={statusBadge.color} style={statusBadge.style}>
                          {order.status}
                        </Badge>
                      </div>
                      <div className={styles.orderDates}>
                        <CalendarRegular fontSize={16} />
                        <span>{formatDate(order.starts_at)} — {formatDate(order.stops_at)}</span>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}

        {selectedTab === 'history' && (
          <div className={styles.value}>No order history</div>
        )}

        {selectedTab === 'notes' && (
          <div className={styles.value}>No notes</div>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          appearance="outline"
          className={styles.viewButton}
          onClick={handleViewCustomer}
        >
          View customer
        </Button>
      </div>
    </div>
    </FluentProvider>
    </Portal>
  );
};

export default CustomerHoverCard;
